import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Shield, Users, CheckCircle, XCircle,
} from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { FeatureStatusBanner } from "@/components/sartn/FeatureStatusBanner";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { TelegramConnectionGuide } from "@/components/sartn/TelegramConnectionGuide";
import { useGroupStore } from "@/stores/groupStore";
import { cn } from "@/lib/cn";
import type { GroupConfig } from "@/stores/groupStore";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const LIST_CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const LIST_ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: SPRING },
};

function navigateToGroupSettings(groupId: string) {
  window.location.hash = `/groups/${groupId}`;
}

export default function GroupsPage() {
  const { groups, isLoading, fetchGroups } = useGroupStore();

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const countBadge = groups.length > 0 && (
    <span
      className="flex h-6 min-w-6 items-center justify-center rounded-full
                 bg-[var(--color-primary)] px-1.5 text-xs font-semibold text-white"
    >
      {groups.length}
    </span>
  );

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Groups" onBack={() => window.history.back()} rightAction={countBadge} />

      <main className="flex-1 px-4 pb-20 pt-2">
        <FeatureStatusBanner
          status="limited"
          title="Live groups, partial settings / ቀጥታ ግሩፖች፣ ከፊል ቅንብሮች"
          description="Group discovery and welcome messages use the API. Advanced moderation toggles remain session-only. / ግሩፕ መፈለጊያና የእንኳን ደህና መጡ መልዕክት API ይጠቀማሉ፤ የላቁ ቁጥጥሮች ለአሁኑ ክፍለ ጊዜ ብቻ ናቸው።"
        />

        {isLoading && <LoadingSkeleton variant="list" count={3} className="mt-2" />}

        {!isLoading && groups.length === 0 && (
          <TelegramConnectionGuide kind="group" />
        )}

        {!isLoading && groups.length > 0 && (
          <motion.div
            variants={LIST_CONTAINER_VARIANTS}
            initial="hidden"
            animate="visible"
            className="mt-2 flex flex-col gap-3"
          >
            {groups.map((group) => (
              <motion.div key={group.id} variants={LIST_ITEM_VARIANTS}>
                <GroupCard
                  group={group}
                  onClick={() => navigateToGroupSettings(group.id)}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>
    </div>
  );
}

function GroupCard({ group, onClick }: { group: GroupConfig; onClick: () => void }) {
  const activeFeatures = countActiveFeatures(group);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      className={cn(
        "flex w-full flex-col gap-3 rounded-[var(--radius-lg)] p-4",
        "bg-[var(--color-surface)] shadow-sm text-left transition-colors",
        "active:bg-[var(--color-border-subtle)]",
      )}
      aria-label={`Manage ${group.title}`}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
            "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
          )}
        >
          <Users className="h-5 w-5" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate text-sm font-semibold text-[var(--color-text)]">
            {group.title}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--color-text-muted)]">
              {group.memberCount} members
            </span>
            <AdminBadge isAdmin={group.botIsAdmin} />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
        <span>{group.stats.messagesModerated} moderated</span>
        <span aria-hidden="true">&middot;</span>
        <span>{group.stats.warningsIssued} warnings</span>
        <span aria-hidden="true">&middot;</span>
        <span>{group.stats.usersBanned} bans</span>
      </div>

      <FeatureDots activeCount={activeFeatures} />
    </motion.button>
  );
}

function AdminBadge({ isAdmin }: { isAdmin: boolean }) {
  if (isAdmin) {
    return (
      <span className="inline-flex items-center gap-1 rounded-[var(--radius-full)] bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
        <CheckCircle className="h-3 w-3" />
        Admin
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-[var(--radius-full)] bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">
      <XCircle className="h-3 w-3" />
      Not Admin
    </span>
  );
}

function FeatureDots({ activeCount }: { activeCount: number }) {
  const labels = ["Spam", "Flood", "Words", "Links"];
  const maxDots = labels.length;

  return (
    <div className="flex items-center gap-2">
      {labels.map((label, index) => {
        const isActive = index < activeCount;
        return (
          <div key={label} className="flex items-center gap-1">
            <div
              className={cn(
                "h-2 w-2 rounded-full",
                isActive ? "bg-emerald-600" : "bg-[var(--color-border)]",
              )}
            />
            <span
              className={cn(
                "text-[10px]",
                isActive
                  ? "text-[var(--color-text-secondary)]"
                  : "text-[var(--color-text-muted)]",
              )}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function countActiveFeatures(group: GroupConfig): number {
  const { antiSpamEnabled, antiFloodEnabled, badWordsEnabled, linkProtectionEnabled } =
    group.settings;
  return [antiSpamEnabled, antiFloodEnabled, badWordsEnabled, linkProtectionEnabled].filter(
    Boolean,
  ).length;
}
